import {
  inject,
  isDevMode,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core';
import {
  Analytics,
  getAnalytics,
  provideAnalytics,
  ScreenTrackingService,
  UserTrackingService,
} from '@fido/firebase/analytics';
import { initializeApp, provideFirebaseApp } from '@fido/firebase/app';
import {
  initializeAppCheck,
  provideAppCheck,
  ReCaptchaEnterpriseProvider,
} from '@fido/firebase/app-check';
import { connectAuthEmulator, getAuth, provideAuth } from '@fido/firebase/auth';
import { connectFirestoreEmulator, getFirestore, provideFirestore } from '@fido/firebase/firestore';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@fido/firebase/functions';
import { getMessaging, provideMessaging } from '@fido/firebase/messaging';
import { getPerformance, Performance, providePerformance } from '@fido/firebase/performance';
import { connectStorageEmulator, getStorage, provideStorage } from '@fido/firebase/storage';
import { environment } from '../../environments/environment';

export function provideFirebase() {
  return makeEnvironmentProviders([
    provideFirebaseApp(() => initializeApp(environment.firebase.options)),
    provideAuth(() => {
      const auth = getAuth();
      if (isDevMode()) connectAuthEmulator(auth, 'http://127.0.0.1:9099');
      return auth;
    }),
    provideFirebaseAnalytics(),
    provideFirebasePerformanceMonitoring(),
    provideAppCheck(() =>
      initializeAppCheck(undefined, {
        provider: new ReCaptchaEnterpriseProvider(environment.firebase.appCheck.siteKey),
        isTokenAutoRefreshEnabled: true,
      }),
    ),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (isDevMode()) connectFirestoreEmulator(firestore, '127.0.0.1', 8080);
      return firestore;
    }),
    provideFunctions(() => {
      const functions = getFunctions();
      if (isDevMode()) connectFunctionsEmulator(functions, '127.0.0.1', 5001);
      return functions;
    }),
    provideMessaging(() => getMessaging()),
    provideStorage(() => {
      const storage = getStorage();
      if (isDevMode()) connectStorageEmulator(storage, '127.0.0.1', 9199);
      return storage;
    }),
  ]);
}

function provideFirebaseAnalytics() {
  return makeEnvironmentProviders([
    provideAnalytics(() => getAnalytics()),
    ScreenTrackingService,
    UserTrackingService,
    provideEnvironmentInitializer(() => inject(Analytics)),
  ]);
}

function provideFirebasePerformanceMonitoring() {
  return makeEnvironmentProviders([
    providePerformance(() => getPerformance()),
    provideEnvironmentInitializer(() => inject(Performance)),
  ]);
}
